import { type ReactElement, useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { type EmailLogEntry, getEmailLog } from "../../lib/api";
import { cn } from "../../lib/cn";
import styles from "./EmailLogPage.module.css";

/**
 * Outbound email log (/admin/email-log), read-only.
 *
 * Every sendMail attempt the server records: which template fired, the
 * outcome (sent / failed / logged when SMTP is unconfigured in dev), SMTP's
 * response or the error, and attachment metadata. Recipients and actors are
 * masked at write time and body previews are redacted, so nothing sensitive
 * renders here. Rows expand to show the diagnostic details.
 */

const KIND_LABELS: Record<string, string> = {
  portal_invite: "Portal invite",
  password_reset: "Password reset",
  quote_delivery: "Quote delivery",
  invoice_delivery: "Invoice delivery",
  mention: "Mention",
  quote_decision: "Quote decision",
  portal_job_created: "New portal job",
  payment_receipt: "Payment receipt",
  template_test: "Template test",
  other: "Other",
};

const STATUS_FILTERS = [
	{ value: "", label: "All outcomes" },
	{ value: "sent", label: "Sent" },
	{ value: "failed", label: "Failed" },
	{ value: "logged", label: "Logged (dev)" },
];

const PAGE_SIZE = 50;

function kindLabel(kind: string): string {
	return KIND_LABELS[kind] ?? kind;
}

function formatDateTime(value: string): string {
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatBytes(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function EmailLogPage() {
	const { canManageUsers } = useAuth();

	const [entries, setEntries] = useState<EmailLogEntry[] | null>(null);
	const [statusFilter, setStatusFilter] = useState("");
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const applyFilter = useCallback(async (status: string): Promise<void> => {
		setError(null);
		try {
			const body = await getEmailLog({
				limit: PAGE_SIZE,
				status: status || undefined,
			});
			setEntries(body.entries);
			setExpandedId(null);
		} catch (loadError) {
			setError(
				loadError instanceof Error
					? loadError.message
					: "Could not load the email log",
			);
		}
	}, []);

	useEffect(() => {
		if (!canManageUsers) return;
		void applyFilter(statusFilter);
	}, [canManageUsers, applyFilter, statusFilter]);

	const loadMore = async (): Promise<void> => {
		if (!entries || loadingMore || entries.length === 0) return;
		setLoadingMore(true);
		setError(null);
		try {
			const oldest = entries[entries.length - 1];
			const body = await getEmailLog({
				limit: PAGE_SIZE,
				before: oldest.createdAt,
				status: statusFilter || undefined,
			});
			setEntries([...entries, ...body.entries]);
		} catch (loadError) {
			setError(
				loadError instanceof Error
					? loadError.message
					: "Could not load older entries",
			);
		} finally {
			setLoadingMore(false);
		}
	};

	if (!canManageUsers) {
		return (
			<div className={styles.root}>
				<div className={styles.head}>
					<h1 className={styles.title}>Email log</h1>
				</div>
				<p className={styles.deny} role="alert">
					You do not have permission to manage users.
				</p>
			</div>
		);
	}

	const showLoadMore = entries !== null && entries.length >= PAGE_SIZE;
	return (
		<div className={styles.root}>
			<div className={styles.head}>
				<div className={styles.headline}>
					<h1 className={styles.title}>Email log</h1>
					<p className={styles.lede}>
						Every outbound email the system attempted, newest first — sends,
						failures, and development-mode logs. Recipients are partially hidden
						and links are stripped of their secret tokens, so nothing sensitive
						is shown here.
					</p>
				</div>
				<div className={styles.headerActions}>
					<button
						type="button"
						className={styles.secondaryButton}
						onClick={() => void applyFilter(statusFilter)}
						disabled={entries === null}
					>
						Refresh
					</button>
				</div>
			</div>

			<div className={styles.toolbar}>
				<select
					className={styles.filter}
					aria-label="Filter by outcome"
					value={statusFilter}
					onChange={(event) => {
						const status = event.currentTarget.value;
						setStatusFilter(status);
						void applyFilter(status);
					}}
				>
					{STATUS_FILTERS.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</div>

			{error && (
				<p className={styles.actionError} role="alert">
					{error}
				</p>
			)}

			{entries === null ? (
				<p className={styles.empty}>Loading email log…</p>
			) : entries.length === 0 ? (
				<p className={styles.empty}>
					No emails recorded yet. Sends appear here once the system emails a
					client, staff member or portal user.
				</p>
			) : (
				<>
					<div className={styles.tableFrame}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th scope="col">When</th>
									<th scope="col">Kind</th>
									<th scope="col">Outcome</th>
									<th scope="col">To</th>
									<th scope="col">Subject</th>
									<th scope="col">Attachment</th>
								</tr>
							</thead>
							<tbody>
								{entries.map((entry) => {
									const expanded = expandedId === entry.id;
									return (
										<LogEntryRows
											key={entry.id}
											entry={entry}
											expanded={expanded}
											onToggle={() => setExpandedId(expanded ? null : entry.id)}
										/>
									);
								})}
							</tbody>
						</table>
					</div>
					{showLoadMore && (
						<div className={styles.pager}>
							<button
								type="button"
								className={styles.secondaryButton}
								onClick={() => void loadMore()}
								disabled={loadingMore}
							>
								{loadingMore ? "Loading…" : "Load older"}
							</button>
						</div>
					)}
				</>
			)}
		</div>
	);
}

/** One log entry row with its optional expandable diagnostics row. */
function LogEntryRows({
	entry,
	expanded,
	onToggle,
}: {
	entry: EmailLogEntry;
	expanded: boolean;
	onToggle: () => void;
}): ReactElement {
	return (
		<>
			<tr className={styles.row} onClick={onToggle} aria-expanded={expanded}>
				<td data-label="When">{formatDateTime(entry.createdAt)}</td>
				<td data-label="Kind">{kindLabel(entry.kind)}</td>
				<td data-label="Outcome">
					<span
						className={cn(
							styles.badge,
							entry.status === "failed" && styles.badgeFailed,
						)}
					>
						{entry.status}
					</span>
				</td>
				<td data-label="To">{entry.to.join(", ") || "—"}</td>
				<td data-label="Subject">{entry.subject || "—"}</td>
				<td data-label="Attachment">
					{entry.attachmentName
						? `${entry.attachmentName}${
								entry.attachmentBytes !== null
									? ` (${formatBytes(entry.attachmentBytes)})`
									: ""
							}`
						: "—"}
				</td>
			</tr>
			{expanded && (
				<tr className={styles.detailRow}>
					<td colSpan={6}>
						<dl className={styles.detailList}>
							<div>
								<dt>Triggered by</dt>
								<dd>{entry.createdBy ?? "system"}</dd>
							</div>
							<div>
								<dt>HTML part</dt>
								<dd>{entry.hasHtml ? "yes" : "no"}</dd>
							</div>
							{entry.messageId && (
								<div>
									<dt>Message ID</dt>
									<dd>{entry.messageId}</dd>
								</div>
							)}
							{entry.smtpResponse && (
								<div>
									<dt>SMTP response</dt>
									<dd>{entry.smtpResponse}</dd>
								</div>
							)}
							{entry.error && (
								<div>
									<dt>Error</dt>
									<dd>{entry.error}</dd>
								</div>
							)}
							{entry.bodyPreview && (
								<div className={styles.detailWide}>
									<dt>Body preview (redacted)</dt>
									<dd>
										<pre>{entry.bodyPreview}</pre>
									</dd>
								</div>
							)}
						</dl>
					</td>
				</tr>
			)}
		</>
	);
}
